import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import churchConfig from '../churchConfig';

/* ═══════════════════════════════════════════════════
   Church Data Manager — Remote Storage via JSONBin.io
   ═══════════════════════════════════════════════════
   
   SETUP (one-time, takes 2 minutes):
   1. Go to https://jsonbin.io and create a free account
   2. Go to API Keys → copy your X-Access-Key
   3. Create a new bin with any content (e.g. {})
   4. Copy the Bin ID from the URL
   5. Paste both values below
   
   HOW IT WORKS:
   - On page load: fetches latest data from JSONBin
   - Admin saves: PUTs updated data to JSONBin
   - Changes are live INSTANTLY for all visitors
   - No files to manage, no deployment needed
   - Free tier: 10,000 requests/month (plenty for a church)

   FALLBACK:
   - If JSONBin is not configured or unavailable,
     falls back to churchConfig.js defaults
   - Also tries public/churchData.json as local override
   ═══════════════════════════════════════════════════ */

// ── CONFIGURATION ──
// Paste your JSONBin credentials here after setup
const JSONBIN_CONFIG = {
  BIN_ID: "69b31027c3097a1dd51d8e35",
  ACCESS_KEY: "$2a$10$Z.YQ6Wb5PAMed2VQhUDeDu0pZSwmkhDpTUHg0teFgK4R.2VXMWkFa",
};

const JSONBIN_API = "https://api.jsonbin.io/v3/b";

// ── Default data from churchConfig ──
function getDefaults() {
  return {
    // ── Church Info (editable identity & contact) ──
    churchInfo: {
      tagline: churchConfig.tagline,
      subtitle: churchConfig.subtitle,
      mission: churchConfig.mission,
      vision: churchConfig.vision,
      coreValues: churchConfig.coreValues.join(", "),
      monthlyVerseRef: churchConfig.monthlyVerse.reference,
      monthlyVerseText: churchConfig.monthlyVerse.text,
      phone: churchConfig.phone,
      email: churchConfig.email,
      website: churchConfig.website,
      eGiving: churchConfig.eGiving,
      street: churchConfig.address.street,
      city: churchConfig.address.city,
      state: churchConfig.address.state,
      zip: churchConfig.address.zip,
      facebookPageUrl: churchConfig.facebookPageUrl,
      youtubeHandle: churchConfig.YOUTUBE_HANDLE,
      youtubeChannelId: churchConfig.YOUTUBE_CHANNEL_ID,
    },
    leaders: churchConfig.leaders.map((l, i) => ({ ...l, id: `staff-${i}`, photo: null })),
    ministries: churchConfig.ministries.map((m, i) => ({ ...m, id: `min-${i}` })),
    newsItems: churchConfig.newsItems.map((n, i) => ({ ...n, id: `news-${i}` })),
    services: churchConfig.services.map((s, i) => ({ ...s, id: `svc-${i}` })),
    heroServices: churchConfig.heroServices.map((s, i) => ({ ...s, id: `hero-${i}` })),
    // ── Giving / Donations ──
    givingEnabled: true,
    paymentMethods: [
      {
        id: "pay-0",
        type: "text-to-give",
        label: "Text-to-Give",
        instructions: "Text NPBC to 73256",
        icon: "📱",
        enabled: true,
      },
    ],
    fundraisers: [],
    // ── Media & Documents ──
    mediaEnabled: true,
    photos: [],       // { id, title, image (base64), caption, enabled }
    documents: [],    // { id, title, description, fileUrl, fileName, fileType, staffOnly, enabled }
    // ── Join Us Form Config ──
    formConfig: {
      heading: "Get In Touch",
      subheading: "Fill out the form below and Pastor Trey or a member of our team will reach out to you.",
      successMessage: "We've received your message and will be in touch soon. We look forward to welcoming you!",
      submitLabel: "Send Message",
      // Email delivery config
      emailService: "none", // "none" | "formspree" | "web3forms" | "emailjs" | "mailto"
      formspreeId: "",       // e.g. "xpzvqkdl"
      web3formsKey: "",      // access key from web3forms.com
      emailjsServiceId: "",  // from emailjs.com
      emailjsTemplateId: "",
      emailjsPublicKey: "",
      mailtoAddress: "",     // fallback mailto:
      fields: [
        { id: "f-0", label: "First Name", type: "text", placeholder: "John", required: true, halfWidth: true, enabled: true },
        { id: "f-1", label: "Last Name", type: "text", placeholder: "Doe", required: true, halfWidth: true, enabled: true },
        { id: "f-2", label: "Email Address", type: "email", placeholder: "john@example.com", required: true, halfWidth: false, enabled: true },
        { id: "f-3", label: "Phone Number", type: "tel", placeholder: "(318) 555-1234", required: false, halfWidth: false, enabled: true },
        { id: "f-4", label: "I'm Interested In", type: "select", placeholder: "Select an option...", required: false, halfWidth: false, enabled: true, options: "Visiting for the first time, Becoming a member, Baptism, Celebrate Recovery, Children's Ministry, Youth Ministry, Volunteering, Prayer request, Other" },
        { id: "f-5", label: "Message", type: "textarea", placeholder: "Tell us a little about yourself or ask any questions...", required: false, halfWidth: false, enabled: true },
      ],
    },
  };
}

// ── Remote Storage Functions ──
async function fetchRemoteData() {
  if (!JSONBIN_CONFIG.BIN_ID || !JSONBIN_CONFIG.ACCESS_KEY) return null;
  try {
    const res = await fetch(`${JSONBIN_API}/${JSONBIN_CONFIG.BIN_ID}/latest`, {
      headers: { "X-Access-Key": JSONBIN_CONFIG.ACCESS_KEY },
    });
    if (!res.ok) return null;
    const json = await res.json();
    return json.record || null;
  } catch (err) {
    console.warn("JSONBin fetch failed:", err.message);
    return null;
  }
}

async function saveRemoteData(data) {
  if (!JSONBIN_CONFIG.BIN_ID || !JSONBIN_CONFIG.ACCESS_KEY) {
    throw new Error("JSONBin not configured");
  }
  const res = await fetch(`${JSONBIN_API}/${JSONBIN_CONFIG.BIN_ID}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      "X-Access-Key": JSONBIN_CONFIG.ACCESS_KEY,
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Save failed: ${res.status} ${text}`);
  }
  return true;
}

// ── Local fallback: try public/churchData.json ──
async function fetchLocalData() {
  try {
    const res = await fetch(`${process.env.PUBLIC_URL}/churchData.json`);
    if (res.ok) return await res.json();
  } catch {}
  return null;
}

// ── Context ──
const ChurchDataContext = createContext();

export function ChurchDataProvider({ children }) {
  const [data, setData] = useState(getDefaults());
  const [loaded, setLoaded] = useState(false);
  const [saveStatus, setSaveStatus] = useState("idle"); // "idle" | "saving" | "saved" | "error"
  const [isRemoteConfigured, setIsRemoteConfigured] = useState(false);

  // On mount: load data (remote → local → defaults)
  useEffect(() => {
    async function init() {
      // Try remote first
      const remote = await fetchRemoteData();
      if (remote && (remote.leaders || remote.newsItems || remote.ministries)) {
        setData(prev => ({
          churchInfo: remote.churchInfo ? { ...prev.churchInfo, ...remote.churchInfo } : prev.churchInfo,
          leaders: remote.leaders || prev.leaders,
          ministries: remote.ministries || prev.ministries,
          newsItems: remote.newsItems || prev.newsItems,
          services: remote.services || prev.services,
          heroServices: remote.heroServices || prev.heroServices,
          givingEnabled: remote.givingEnabled !== undefined ? remote.givingEnabled : prev.givingEnabled,
          paymentMethods: remote.paymentMethods || prev.paymentMethods,
          fundraisers: remote.fundraisers || prev.fundraisers,
          mediaEnabled: remote.mediaEnabled !== undefined ? remote.mediaEnabled : prev.mediaEnabled,
          photos: remote.photos || prev.photos,
          documents: remote.documents || prev.documents,
          formConfig: remote.formConfig ? { ...prev.formConfig, ...remote.formConfig } : prev.formConfig,
        }));
        setIsRemoteConfigured(true);
        setLoaded(true);
        return;
      }

      // Check if JSONBin is configured but empty (first use)
      if (JSONBIN_CONFIG.BIN_ID && JSONBIN_CONFIG.ACCESS_KEY) {
        setIsRemoteConfigured(true);
      }

      // Try local file
      const local = await fetchLocalData();
      if (local) {
        setData(prev => ({
          churchInfo: local.churchInfo ? { ...prev.churchInfo, ...local.churchInfo } : prev.churchInfo,
          leaders: local.leaders || prev.leaders,
          ministries: local.ministries || prev.ministries,
          newsItems: local.newsItems || prev.newsItems,
          services: local.services || prev.services,
          heroServices: local.heroServices || prev.heroServices,
          givingEnabled: local.givingEnabled !== undefined ? local.givingEnabled : prev.givingEnabled,
          paymentMethods: local.paymentMethods || prev.paymentMethods,
          fundraisers: local.fundraisers || prev.fundraisers,
          mediaEnabled: local.mediaEnabled !== undefined ? local.mediaEnabled : prev.mediaEnabled,
          photos: local.photos || prev.photos,
          documents: local.documents || prev.documents,
          formConfig: local.formConfig ? { ...prev.formConfig, ...local.formConfig } : prev.formConfig,
        }));
      }

      setLoaded(true);
    }
    init();
  }, []);

  // ── CRUD operations ──
  const setField = useCallback((key, value) => {
    setData(prev => ({ ...prev, [key]: value }));
    setSaveStatus("idle");
  }, []);

  const updateItem = useCallback((collection, id, updates) => {
    setData(prev => ({
      ...prev,
      [collection]: prev[collection].map(item =>
        item.id === id ? { ...item, ...updates } : item
      ),
    }));
    setSaveStatus("idle");
  }, []);

  const addItem = useCallback((collection, item) => {
    const id = `${collection}-${Date.now()}`;
    setData(prev => ({
      ...prev,
      [collection]: [...prev[collection], { ...item, id }],
    }));
    setSaveStatus("idle");
    return id;
  }, []);

  const removeItem = useCallback((collection, id) => {
    setData(prev => ({
      ...prev,
      [collection]: prev[collection].filter(item => item.id !== id),
    }));
    setSaveStatus("idle");
  }, []);

  const reorderItem = useCallback((collection, fromIndex, toIndex) => {
    setData(prev => {
      const items = [...prev[collection]];
      const [moved] = items.splice(fromIndex, 1);
      items.splice(toIndex, 0, moved);
      return { ...prev, [collection]: items };
    });
    setSaveStatus("idle");
  }, []);

  // ── Save: remote if configured, otherwise download JSON ──
  const saveData = useCallback(async () => {
    setSaveStatus("saving");
    try {
      if (isRemoteConfigured) {
        try {
          await saveRemoteData(data);
          setSaveStatus("saved");
          setTimeout(() => setSaveStatus("idle"), 3000);
          return;
        } catch (err) {
          const jsonSize = new Blob([JSON.stringify(data)]).size;
          const sizeKB = Math.round(jsonSize / 1024);
          console.error("Remote save failed:", err, `Payload: ${sizeKB}KB`);
          const msg = `Remote save failed (data: ${sizeKB}KB).\n\nDownload as local file instead?`;
          if (window.confirm(msg)) {
            downloadJson(data);
            setSaveStatus("saved");
            setTimeout(() => setSaveStatus("idle"), 3000);
          } else {
            setSaveStatus("error");
            setTimeout(() => setSaveStatus("idle"), 5000);
          }
          return;
        }
      }
      // No remote — download JSON
      downloadJson(data);
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 3000);
    } catch (err) {
      console.error("Save failed:", err);
      alert("Save error: " + err.message);
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 5000);
    }
  }, [data, isRemoteConfigured]);

  function downloadJson(d) {
    try {
      const json = JSON.stringify(d, null, 2);
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "churchData.json";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed:", err);
      alert("Failed to create download. Error: " + err.message);
    }
  }

  // ── Export as backup file (always available) ──
  const exportBackup = useCallback(() => {
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `churchData-backup-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [data]);

  // ── Reset to defaults ──
  const resetToDefaults = useCallback(() => {
    setData(getDefaults());
    setSaveStatus("idle");
  }, []);

  return (
    <ChurchDataContext.Provider value={{
      data, loaded, saveStatus, isRemoteConfigured,
      setField, updateItem, addItem, removeItem, reorderItem,
      saveData, exportBackup, resetToDefaults,
    }}>
      {children}
    </ChurchDataContext.Provider>
  );
}

export function useChurchData() {
  return useContext(ChurchDataContext);
}

// Helper: returns churchConfig values with admin overrides applied
export function useChurchInfo() {
  const { data } = useContext(ChurchDataContext);
  const info = data.churchInfo || {};
  return {
    tagline: info.tagline || churchConfig.tagline,
    subtitle: info.subtitle || churchConfig.subtitle,
    mission: info.mission || churchConfig.mission,
    vision: info.vision || churchConfig.vision,
    coreValues: info.coreValues ? info.coreValues.split(",").map(v => v.trim()).filter(Boolean) : churchConfig.coreValues,
    monthlyVerse: {
      reference: info.monthlyVerseRef || churchConfig.monthlyVerse.reference,
      text: info.monthlyVerseText || churchConfig.monthlyVerse.text,
    },
    phone: info.phone || churchConfig.phone,
    email: info.email || churchConfig.email,
    website: info.website || churchConfig.website,
    eGiving: info.eGiving || churchConfig.eGiving,
    address: {
      street: info.street || churchConfig.address.street,
      city: info.city || churchConfig.address.city,
      state: info.state || churchConfig.address.state,
      zip: info.zip || churchConfig.address.zip,
    },
    facebookPageUrl: info.facebookPageUrl || churchConfig.facebookPageUrl,
    youtubeHandle: info.youtubeHandle || churchConfig.YOUTUBE_HANDLE,
    youtubeChannelId: info.youtubeChannelId || churchConfig.YOUTUBE_CHANNEL_ID,
    // Pass-through static values
    name: churchConfig.name,
    shortName: churchConfig.shortName,
    history: churchConfig.history,
  };
}
